import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import clsx from "clsx";
import { motion, useAnimation } from "framer-motion";
import { groupBy, omit, remove } from "lodash";
import { AsyncPaginate } from "react-select-async-paginate";

import { type Option } from "@/types/react-select";
import { Loading } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import {
  FETCH_ORDER_TAG_SUBSCRIPTION_ASSOCIATIONS,
  FETCH_PAGINATED_ORDER_TAGS,
  UPSERT_ORDER_TAG_SUBSCRIPTION_ASSOCIATIONS,
} from "@gql/subscription-order-tags";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  SubscriptionTypeEnum,
  type FetchOrderTagSubscriptionAssociations,
  type FetchPaginatedOrderTags,
  type FetchPaginatedOrderTagsVariables,
  type UpsertOrderTagSubscriptionAssociations,
  type UpsertOrderTagSubscriptionAssociationsVariables,
} from "@north-beam/nb-client-side";
import { IconButton } from "@shared/buttons";
import { Select } from "@shared/selects";
import { renderChevronAnimation } from "@shared/selects/utils";

type OrderTagOptions = Option<
  FetchPaginatedOrderTags["me"]["paginatedOrderTags"]["orderTags"][number]["tag"]
>[];

export function SubscriptionOrderTags() {
  const [expandedHelp, setExpandedHelp] = useState(false);

  return (
    <div className="w-full pr-10">
      <div className="text-3xl font-semibold">Subscription order tags</div>
      <div className="border-t w-full mt-3" />
      <div className="rounded-md border mt-10">
        <div
          className={clsx(
            "flex items-center h-10 px-3 bg-white rounded-t-md cursor-pointer",
            {
              "rounded-b-md": !expandedHelp,
            },
          )}
          onClick={() => setExpandedHelp(!expandedHelp)}
        >
          <div>
            <i className="fa-regular fa-sharp fa-circle-question mr-1 text-primary" />{" "}
            How it works
          </div>
          <i
            className={clsx("fa-regular fa-sharp mr-2 text-primary ml-auto", {
              "fa-chevron-down": expandedHelp,
              "fa-chevron-up": !expandedHelp,
            })}
          />
        </div>
        <Collapsible expanded={expandedHelp}>
          <div className="ml-2 mt-5">
            Please read more{" "}
            <a
              href="https://docs.northbeam.io/docs/subscription-analytics"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>{" "}
            to learn about Northbeam Subscription Analytics
          </div>
          <iframe
            src="https://www.youtube.com/embed/n40dAVAn6B0"
            title="Subscription order tags help video"
            width="100%"
            height="500"
            className="mt-5 mb-5"
          />
        </Collapsible>
      </div>
      <SubscriptionOrderTagsInput />
    </div>
  );
}

export const SubscriptionLabelToValueMapping = {
  "First Time": SubscriptionTypeEnum.FIRST_TIME,
  Recurring: SubscriptionTypeEnum.RETURNING,
  Other: SubscriptionTypeEnum.OTHER,
  "Non-subscription": SubscriptionTypeEnum.NONE,
} as const;
export const SubscriptionValueToLabelMapping = {
  [SubscriptionTypeEnum.FIRST_TIME]: "First Time",
  [SubscriptionTypeEnum.RETURNING]: "Recurring",
  [SubscriptionTypeEnum.OTHER]: "Other",
  [SubscriptionTypeEnum.NONE]: "Non-subscription",
} as const;
export const SUBSCRIPTION_TYPES: Option<SubscriptionTypeEnum>[] = [
  { label: "First Time", value: SubscriptionTypeEnum.FIRST_TIME },
  { label: "Recurring", value: SubscriptionTypeEnum.RETURNING },
  { label: "Other", value: SubscriptionTypeEnum.OTHER },
  { label: "Non-subscription", value: SubscriptionTypeEnum.NONE },
];

const USABLE_SUBSCRIPTION_TYPES = SUBSCRIPTION_TYPES.filter(
  (sub) => sub.value !== SubscriptionTypeEnum.NONE,
);
function SubscriptionOrderTagsInput() {
  const [subscriptionCategory, setSubscriptionCategory] = useState<
    (typeof USABLE_SUBSCRIPTION_TYPES)[number]["value"] | null
  >(USABLE_SUBSCRIPTION_TYPES[0].value);

  const [selectedOrderTags, setSelectedOrderTags] = useState<OrderTagOptions>(
    [],
  );
  const {
    data,
    loading: loadingFetch,
    refetch,
  } = useNorthbeamQuery<FetchOrderTagSubscriptionAssociations>(
    FETCH_ORDER_TAG_SUBSCRIPTION_ASSOCIATIONS,
    { variables: {}, notifyOnNetworkStatusChange: true },
  );
  const allOrderTagAssociations = useMemo(
    () =>
      (data?.me.orderTagSubscriptionAssociations ?? []).map((association) =>
        omit(association, "__typename"),
      ),
    [data?.me.orderTagSubscriptionAssociations],
  );

  const [upsertOrderTagSubscriptions, { loading: loadingUpsert }] =
    useNorthbeamMutation<
      UpsertOrderTagSubscriptionAssociations,
      UpsertOrderTagSubscriptionAssociationsVariables
    >(UPSERT_ORDER_TAG_SUBSCRIPTION_ASSOCIATIONS, {
      onCompleted() {
        refetch();
      },
    });

  const subscriptionToOrderTagMapping = useMemo(() => {
    return groupBy(
      data?.me.orderTagSubscriptionAssociations,
      "subscriptionType",
    );
  }, [data?.me.orderTagSubscriptionAssociations]);

  const onDelete = (
    orderTag: UpsertOrderTagSubscriptionAssociationsVariables["associations"][number]["orderTag"],
  ) => {
    const filteredAssociations = remove(
      allOrderTagAssociations,
      (association) => association.orderTag !== orderTag,
    );
    upsertOrderTagSubscriptions({
      variables: { associations: filteredAssociations },
    });
  };

  const onAdd = (
    newOrderTagAssocations: UpsertOrderTagSubscriptionAssociationsVariables["associations"],
  ) => {
    upsertOrderTagSubscriptions({
      variables: {
        associations: [...allOrderTagAssociations, ...newOrderTagAssocations],
      },
    });
    setSelectedOrderTags([]);
  };

  const loading = loadingFetch || loadingUpsert;
  const exclusionList = useMemo(
    () => allOrderTagAssociations.map(({ orderTag }) => orderTag),
    [allOrderTagAssociations],
  );

  return (
    <div className="mt-10">
      <div className="text-lg font-semibold">Search and select order tags</div>
      <div className="flex space-x-4">
        <OrderTagsSelector
          exclusionList={exclusionList}
          onChange={setSelectedOrderTags}
          value={selectedOrderTags}
        />
        <Select
          options={USABLE_SUBSCRIPTION_TYPES}
          onChange={setSubscriptionCategory}
          value={subscriptionCategory}
          label="Subscription category"
          maxWidth="12rem"
          name="subscription-category"
        />
        <IconButton
          className="self-end"
          variant="primary"
          isDisabled={!selectedOrderTags?.length || !subscriptionCategory}
          onClick={() => {
            // early exit if someone we get into this error state
            if (!selectedOrderTags?.length || !subscriptionCategory) {
              return;
            }

            const associations: UpsertOrderTagSubscriptionAssociationsVariables["associations"] =
              selectedOrderTags.map((orderTagOption) => ({
                subscriptionType: subscriptionCategory,
                orderTag: orderTagOption.value,
              }));
            onAdd(associations);
          }}
          leftIcon={<i className="fa-regular fa-sharp fa-plus" />}
        >
          ADD
        </IconButton>
      </div>
      <div className="mt-10 w-full">
        <div className="text-lg font-semibold">Mapped order tags</div>
        <div className="mt-5">
          Map any subscription order tags to Northbeam&apos;s subscription
          categories. If you dont know if the tag relates to &apos;first
          time&apos; or &apos;recurring&apos;, put it in the &apos;other&apos;
          category.
        </div>
        <div>
          Any updates to your mappings will only be reflected after your next
          data refresh.
        </div>
        <div className="relative">
          {loading && <Loading />}
          <table
            className={clsx("table-auto w-full mt-6 bg-white border", {
              "opacity-25": loading,
            })}
          >
            <thead>
              <tr
                className="h-[4rem] font-semibold	border-b"
                style={{
                  background:
                    "linear-gradient(0deg, rgba(255, 255, 255, 0.95), " +
                    "rgba(255, 255, 255, 0.95)), #717680",
                }}
              >
                <th className="w-[15rem] font-semibold pl-2 border-r">
                  Subscription category
                </th>
                <th className="font-semibold pl-2">Order tag(s)</th>
              </tr>
            </thead>
            <tbody>
              {USABLE_SUBSCRIPTION_TYPES.map((subscriptionOption) => {
                const orderTagAssociations =
                  subscriptionToOrderTagMapping[subscriptionOption.value] ?? [];
                return (
                  <tr
                    key={subscriptionOption.value}
                    className="min-h-[4rem] border-b"
                  >
                    <td className="pl-2 border-r">
                      {subscriptionOption.label}
                    </td>
                    <td className="pl-2 flex flex-row items-center min-h-[4rem] flex-wrap	">
                      {orderTagAssociations.map((orderTagAssociation) => {
                        return (
                          <OrderTagPill
                            key={orderTagAssociation.orderTag}
                            value={orderTagAssociation.orderTag}
                            onDelete={onDelete}
                          />
                        );
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function OrderTagPill({
  value,
  onDelete,
}: {
  value: string;
  onDelete: (
    orderTag: UpsertOrderTagSubscriptionAssociationsVariables["associations"][number]["orderTag"],
  ) => void;
}) {
  return (
    <div
      className={clsx("rounded-[3rem] text-[#666666] m-1 py-1 px-5 w-fit  ")}
      style={{
        background:
          "linear-gradient(0deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), #717680",
      }}
    >
      {value}
      <i
        className=" ml-5 fa-regular fa-sharp cursor-pointer text-lg fa-xmark"
        onClick={() => onDelete(value)}
      />
    </div>
  );
}

export function Collapsible({
  children,
  expanded,
}: PropsWithChildren<{ expanded?: boolean }>) {
  const controls = useAnimation();

  const variants = {
    expanded: { opacity: 1, height: "auto" },
    collapsed: { opacity: 0, height: 0 },
  };

  useEffect(() => {
    if (expanded) {
      controls.start("expanded");
    } else {
      controls.start("collapsed");
    }
  }, [controls, expanded]);

  return (
    <motion.div
      initial="collapsed"
      className="z-0 overflow-hidden"
      animate={controls}
      transition={{ duration: 0.3 }}
      variants={variants}
    >
      {children}
    </motion.div>
  );
}

const OrderTagsSelector = ({
  onChange,
  value,
  exclusionList,
}: {
  onChange: (options: OrderTagOptions) => void;
  value: OrderTagOptions;
  exclusionList: string[];
}) => {
  const { user } = useUser();

  const loadOptions = useCallback(
    async (search: string, prevOptions: any) => {
      const limit = 1000;

      const result = await user.apolloClient.query<
        FetchPaginatedOrderTags,
        FetchPaginatedOrderTagsVariables
      >({
        query: FETCH_PAGINATED_ORDER_TAGS,
        variables: {
          offset: prevOptions.length,
          limit,
          search,
          exclusionList,
        },
      });

      const newTags = result.data.me.paginatedOrderTags.orderTags;

      return {
        options: newTags.map(({ tag, count }) => ({
          label: `${tag} - ${count} order${count !== 1 ? "s" : ""}`,
          value: tag,
        })),
        hasMore: !(newTags.length < limit),
      };
    },
    [user.apolloClient, exclusionList],
  );

  return (
    <div className="max-w-lg nb-input-container">
      <label htmlFor="order-tags" className="label">
        Search order tags
      </label>
      <AsyncPaginate
        debounceTimeout={300}
        name="order-tags"
        isDisabled={false}
        value={value}
        onChange={onChange as any}
        isMulti
        placeholder="Search Order Tags..."
        classNames={{
          control: () => "nb-input flex items-center",
          indicatorsContainer: () => "flex items-center",
          menu: () =>
            "absolute max-h-[20rem] overflow-y-auto z-10 mt-2 px-0 py-2 bg-white " +
            "rounded-lg elevation-1 text-sm",
          option: ({ isSelected, isFocused }) =>
            clsx(
              isSelected || isFocused
                ? "bg-cosmic-3 font-semibold"
                : "hover:bg-cosmic-4",
              "px-4 py-2 cursor-pointer text-interstellar font-normal",
            ),
        }}
        components={{
          IndicatorSeparator: () => null,
          ValueContainer: ({ children }) => <>{children}</>,
          DropdownIndicator: ({ isFocused }) => (
            <>{renderChevronAnimation(isFocused, false)}</>
          ),
        }}
        styles={{
          control: () => ({}),
          indicatorsContainer: () => ({}),
          valueContainer: () => ({}),
          container: () => ({}),
        }}
        cacheUniqs={[exclusionList]}
        loadOptions={loadOptions}
        className="w-full"
      />
    </div>
  );
};
